import { useRef, useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSSE } from './useSSE'
import { queryKeys } from '@/lib/queryKeys'
import { updateDagRowInCache } from '@/lib/dagCacheUtils'
import { useDagTableStore } from '@/stores/useDagTableStore'
import type {
  DagSSERowComputing,
  DagSSECellComputed,
  DagSSEAggregateUpdate,
} from '@/types/dag'

interface DagSSERowUpdateCell {
  column_id: string
  value: unknown
  status?: string
}

interface DagSSERowUpdatePayload {
  row_id: string
  cells: Record<string, unknown> | DagSSERowUpdateCell[]
  row_version?: number
  new_version?: number
}

export function useDagSSE(
  planId: string | undefined,
  versionId: string | undefined
) {
  const queryClient = useQueryClient()
  const store = useDagTableStore
  const sseReconnectNonce = useDagTableStore(state => state.sseReconnectNonce)
  const sseEnabled = !!planId && !!versionId

  // Reset computing state when version changes
  const prevVersionRef = useRef(versionId)
  useEffect(() => {
    if (prevVersionRef.current !== versionId) {
      store.getState().reset()
      prevVersionRef.current = versionId
    }
  }, [versionId, store])

  const handleMessage = useCallback(
    (event: { type: string; data: Record<string, unknown> }) => {
      const { data } = event

      const payloadTypeCandidates = [
        data.type,
        data.event,
        data.event_name,
        data.eventType,
      ]

      const payloadType = payloadTypeCandidates.find(
        value => typeof value === 'string' && value.length > 0
      ) as string | undefined

      const eventType =
        event.type && event.type !== 'message' ? event.type : payloadType

      const normalizedType = (eventType ?? '')
        .toLowerCase()
        .replace(/[._-]/g, ':')

      const nestedPayload =
        (data.payload as Record<string, unknown> | undefined) ??
        (data.data as Record<string, unknown> | undefined)

      const payload =
        nestedPayload && typeof nestedPayload === 'object'
          ? nestedPayload
          : data

      switch (normalizedType) {
        case 'row:computing': {
          const rowComputing = payload as unknown as DagSSERowComputing
          store
            .getState()
            .setComputing(rowComputing.row_id, rowComputing.columns)
          break
        }

        case 'cell:computed': {
          const cellComputed = payload as unknown as DagSSECellComputed
          store
            .getState()
            .clearComputing(cellComputed.row_id, cellComputed.column_id)

          // Update the cell value in React Query cache
          updateDagRowInCache(
            queryClient,
            planId!,
            versionId!,
            cellComputed.row_id,
            { [cellComputed.column_id]: cellComputed.value }
          )
          break
        }

        case 'row:update': {
          const rowUpdate = payload as unknown as DagSSERowUpdatePayload
          const normalizedCells = Array.isArray(rowUpdate.cells)
            ? rowUpdate.cells.reduce<Record<string, unknown>>((acc, cell) => {
                if (cell?.column_id) {
                  acc[cell.column_id] = cell.value
                }
                return acc
              }, {})
            : rowUpdate.cells

          const nextRowVersion = rowUpdate.row_version ?? rowUpdate.new_version
          store.getState().clearAllComputing(rowUpdate.row_id)

          // Update entire row in React Query cache
          updateDagRowInCache(
            queryClient,
            planId!,
            versionId!,
            rowUpdate.row_id,
            normalizedCells,
            nextRowVersion
          )
          break
        }

        case 'aggregate:update': {
          const aggregateUpdate = payload as unknown as DagSSEAggregateUpdate
          store.getState().mergeAggregates(aggregateUpdate.aggregates)
          break
        }
      }
    },
    [queryClient, planId, versionId, store]
  )

  const handleReconnect = useCallback(() => {
    store.getState().reset()
    queryClient.invalidateQueries({
      queryKey: queryKeys.dagTable.rowsPrefix(planId!, versionId!),
    })
  }, [queryClient, planId, versionId, store])

  const url =
    planId && versionId
      ? `/plans/${planId}/versions/${versionId}/table/events${sseReconnectNonce > 0 ? `?r=${sseReconnectNonce}` : ''}`
      : null

  useSSE({
    url,
    onMessage: handleMessage,
    onReconnect: handleReconnect,
    enabled: sseEnabled,
    retry: true,
    retryInterval: 3000,
    autoDisconnectOnTerminal: false,
    openWhenHidden: true,
  })
}
